/**
 * Country Constants
 *
 * Master data list for countries following ISO 3166-1 standard.
 * Ordered by prevalence in international trade.
 */

/**
 * Countries list with ISO codes
 * - code: ISO 3166-1 alpha-2 code
 * - name: Country name
 */
export const COUNTRIES = [
  // Major trading partners first
  { code: 'US', name: 'United States' },
  { code: 'CN', name: 'China' },
  { code: 'DE', name: 'Germany' },
  { code: 'JP', name: 'Japan' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'FR', name: 'France' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'KR', name: 'South Korea' },
  { code: 'IT', name: 'Italy' },
  { code: 'CA', name: 'Canada' },
  { code: 'MX', name: 'Mexico' },
  { code: 'IN', name: 'India' },
  { code: 'SG', name: 'Singapore' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'AU', name: 'Australia' },
  { code: 'ES', name: 'Spain' },
  { code: 'BE', name: 'Belgium' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'TH', name: 'Thailand' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'PH', name: 'Philippines' },
  { code: 'BR', name: 'Brazil' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'PL', name: 'Poland' },
  { code: 'SE', name: 'Sweden' },
  { code: 'AT', name: 'Austria' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'TR', name: 'Turkey' },
  { code: 'IE', name: 'Ireland' },
  { code: 'DK', name: 'Denmark' },
  { code: 'FI', name: 'Finland' },
  { code: 'NO', name: 'Norway' },
  { code: 'PT', name: 'Portugal' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'IL', name: 'Israel' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'RU', name: 'Russia' },
  { code: 'HU', name: 'Hungary' },
  { code: 'RO', name: 'Romania' },
  { code: 'CL', name: 'Chile' },
  { code: 'CO', name: 'Colombia' },
  { code: 'AR', name: 'Argentina' },
  { code: 'PE', name: 'Peru' },
  { code: 'EG', name: 'Egypt' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'KE', name: 'Kenya' },
  { code: 'GH', name: 'Ghana' },
  { code: 'MA', name: 'Morocco' },
  { code: 'QA', name: 'Qatar' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'BH', name: 'Bahrain' },
  { code: 'OM', name: 'Oman' },
  { code: 'JO', name: 'Jordan' },
  { code: 'LB', name: 'Lebanon' },
  { code: 'GR', name: 'Greece' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'HR', name: 'Croatia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'RS', name: 'Serbia' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LV', name: 'Latvia' },
  { code: 'EE', name: 'Estonia' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MT', name: 'Malta' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'IS', name: 'Iceland' },
];

/**
 * Country lookup map for O(1) access
 */
export const COUNTRY_MAP = Object.freeze(
  COUNTRIES.reduce((map, country) => {
    map[country.code] = country;
    return map;
  }, {})
);

/**
 * Get country info by code
 * @param {string} code - ISO 3166-1 alpha-2 code
 * @returns {Object|null} Country info or null if not found
 */
export function getCountry(code) {
  if (!code) return null;
  return COUNTRY_MAP[code.toUpperCase()] || null;
}

/**
 * Country options for dropdowns
 */
export const COUNTRY_OPTIONS = [
  { value: '', label: 'Select country...' },
  ...COUNTRIES.map(c => ({
    value: c.code,
    label: c.name
  }))
];

/**
 * Get country name by code
 * @param {string} code - ISO 3166-1 alpha-2 code
 * @returns {string} Country name or the code if not found
 */
export function getCountryName(code) {
  const country = getCountry(code);
  return country?.name || code || '';
}
